import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ButtonBase } from "@mui/material";
import { IconEdit, IconTrash, IconPlus } from "@tabler/icons-react";
import MainCard from "ui-component/cards/MainCard";
import SearchSection from "layout/MainLayout/Header/SearchSection";

const EditButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.secondary.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  width: 40,
  height: 40,
  borderRadius: 10,
  cursor: "pointer",
}));

const DeleteButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.error.dark,
  "&:hover": {
    background: theme.palette.error.dark,
    color: theme.palette.error.light,
  },
  width: 40,
  height: 40,
  borderRadius: 12,
  cursor: "pointer",
}));

const AddButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.info.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  align: "center",
  gap: 8,
  width: 180,
  height: 40,
  borderRadius: 10,
  display: "flex",
  cursor: "pointer",
}));

const MaritalStatusList = () => {
  const [maritalStatuses, setMaritalStatuses] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentMaritalStatus, setCurrentMaritalStatus] = useState({ name: "" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  
  const fetchMaritalStatuses = useCallback(() => {
    const searchText = searchTerm !== "" ? `&contains=${searchTerm}` : "";
    fetch(
      `/backend/marital-status/?page=${
        page + 1
      }&page_size=${rowsPerPage}${searchText}`
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Não foi possível carregar a lista de estados civis. Tente novamente.");
        }
        return response.json();
      })
      .then((data) => {
        setMaritalStatuses(data.results);
        setTotalRecords(data.count);
      })
      .catch((error) => {
        console.error("Erro ao buscar estados civis:", error);
        alert("Não foi possível carregar os estados civis. " + error.message);
      });
  }, [page, rowsPerPage, searchTerm]);

  useEffect(() => {
    fetchMaritalStatuses();
  }, [fetchMaritalStatuses, page, rowsPerPage, searchTerm]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleEdit = (maritalStatus) => {
    setCurrentMaritalStatus(maritalStatus);
    setIsEditing(true);
    setOpenModal(true);
    setFieldErrors({});
  };

  const handleAdd = () => {
    setCurrentMaritalStatus({ name: "" });
    setIsEditing(false);
    setOpenModal(true);
    setFieldErrors({});
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFieldErrors({});
  };

  const handleSave = () => {
    if (!currentMaritalStatus.name.trim()) {
      setFieldErrors({ name: "O campo Nome é obrigatório." });
      return;
    }

    const method = isEditing ? "PUT" : "POST";
    const url = isEditing
      ? `/backend/marital-status/${currentMaritalStatus.id}/`
      : "/backend/marital-status/";

    fetch(url, {
      method: method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(currentMaritalStatus),
    })
      .then(async (response) => {
        if (response.ok) return response.json();
        
        // Tenta extrair os erros específicos de cada campo do backend
        const errors = {};
        let generalError = null;
        
        try {
          const errorData = await response.json();
          
          // Mapeia os erros do backend para os campos do formulário
          const fieldMapping = {
            name: 'name'
          };
          
          // Processa erros de campos específicos
          Object.keys(fieldMapping).forEach(backendField => {
            if (errorData[backendField] && Array.isArray(errorData[backendField])) {
              errors[fieldMapping[backendField]] = errorData[backendField][0];
            }
          });
          
          // Erros gerais
          if (errorData.error) {
            generalError = errorData.error;
          } else if (errorData.non_field_errors && errorData.non_field_errors[0]) {
            generalError = errorData.non_field_errors[0];
          } else if (errorData.detail) {
            generalError = errorData.detail;
          }
          
          // Se houver erros de campos, atualiza o estado
          if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
          }
          
          // Monta mensagem de erro geral
          if (generalError) {
            throw new Error(generalError);
          } else if (Object.keys(errors).length > 0) {
            throw new Error("Por favor, corrija os erros nos campos destacados.");
          } else {
            throw new Error("Não foi possível salvar o estado civil. Verifique os dados e tente novamente.");
          }
        } catch (e) {
          // Se não conseguir parsear o JSON, usa a mensagem padrão
          if (e.message) {
            throw e;
          }
          console.warn("Não foi possível extrair mensagem de erro específica:", e);
          throw new Error("Não foi possível salvar o estado civil. Verifique os dados e tente novamente.");
        }
      })
      .then((data) => {
        if (isEditing) {
          setMaritalStatuses((prevStatuses) =>
            prevStatuses.map((status) =>
              status.id === data.id ? data : status
            )
          );
        } else {
          setMaritalStatuses((prevStatuses) => [...prevStatuses, data]);
        }
        alert("Estado civil cadastrado com sucesso!");
        setOpenModal(false);
        setFieldErrors({});
        fetchMaritalStatuses();
      })
      .catch((error) => alert("Não foi possível salvar o estado civil. " + error.message));
  };

  const handleDelete = (maritalStatusId) => {
    if (window.confirm("Deseja realmente excluir este estado civil? Esta ação não pode ser desfeita.")) {
      fetch(`/backend/marital-status/${maritalStatusId}/`, {
        method: "DELETE",
      })
        .then((response) => {
          if (response.ok) {
            setMaritalStatuses((prevStatuses) =>
              prevStatuses.filter(
                (status) => status.id !== maritalStatusId
              )
            );
            alert("Estado civil excluído com sucesso!");
            fetchMaritalStatuses();
          } else {
            throw new Error("Não foi possível excluir o estado civil. Verifique se não há vínculos com outros registros.");
          }
        })
        .catch((error) => alert("Não foi possível excluir o estado civil. " + error.message));
    }
  };

  return (
    <div className="w-full overflow-auto">
      <MainCard
        title="Cadastro de Estados Civis"
        actions={[
          <AddButton onClick={handleAdd}>
            <IconPlus stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
            Adicionar Estado Civil
          </AddButton>,
        ]}
      >
        <SearchSection onSearchChange={setSearchTerm} />
        <Divider sx={{ my: 2 }} />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {maritalStatuses.map((maritalStatus) => (
              <TableRow key={maritalStatus.id}>
                <TableCell>{maritalStatus.name}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar">
                    <EditButton onClick={() => handleEdit(maritalStatus)}>
                      <IconEdit stroke={1.5} size="1.3rem" />
                    </EditButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <DeleteButton onClick={() => handleDelete(maritalStatus.id)}>
                      <IconTrash stroke={1.5} size="1.3rem" />
                    </DeleteButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MainCard>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Estados civis por página"
      />

      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>
          {isEditing ? "Editar Estado Civil" : "Adicionar Estado Civil"}
        </DialogTitle>
        <DialogContent sx={{ minWidth: 400 }}>
          <TextField
            label="Nome"
            value={currentMaritalStatus.name}
            onChange={(e) => {
              setCurrentMaritalStatus({
                ...currentMaritalStatus,
                name: e.target.value,
              });
              // Limpa o erro do campo quando o usuário começa a digitar
              if (fieldErrors.name) {
                setFieldErrors({ ...fieldErrors, name: null });
              }
            }}
            fullWidth
            sx={{ mt: 2, mb: 2 }}
            required
            error={!!fieldErrors.name}
            helperText={fieldErrors.name || ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleSave} color="primary">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MaritalStatusList;
